import { Injectable, signal, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { extractErrorMessage } from '../../../core/shared/util/error.util';
import { SpinnerService } from '../../../core/shared/services/spinner.service';
import { SnackbarService } from '../../../core/shared/components/snackbar/snackbar.service';
import { StatusDocumento } from '../models/status-documento.model';
import { ListarTiposFeedbacksDocumentoUseCase } from '../usecases/listar-status-documento.usecase';

interface EventoState {
  statusDocumento: StatusDocumento[];
  loading: boolean;
  error: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class EventoViewModel {
  private readonly _state = signal<EventoState>({
    statusDocumento: [],
    loading: false,
    error: null
  });

  readonly state = this._state.asReadonly();
  private readonly spinnerService = inject(SpinnerService);
  private readonly snackbarService = inject(SnackbarService);

  constructor(
    private readonly ListarTiposFeedbacksDocumentoUseCase: ListarTiposFeedbacksDocumentoUseCase
  ) {}

  async carregarStatusDocumento(): Promise<void> {
    this._state.update(state => ({ ...state, loading: true, error: null }));
    this.spinnerService.show();

    try {
      const statusDocumento = await firstValueFrom(this.ListarTiposFeedbacksDocumentoUseCase.execute());
      this._state.update(state => ({ 
        ...state, 
        statusDocumento, 
        loading: false 
      }));
    } catch (error) {
      const msgErro = extractErrorMessage(error, 'Erro ao carregar status de documento!');
      this._state.update(state => ({ 
        ...state, 
        loading: false, 
        error: msgErro
      }));
    } finally {
      this.spinnerService.hide();
    }
  }

  limparMensagens(): void {
    this._state.update(state => ({ 
      ...state, 
      error: null
    }));
  }

  limparStatusDocumento(): void {
    this._state.update(state => ({ 
      ...state, 
      statusDocumento: []
    }));
  }

  /**
   * Busca um feedback pelo código na lista carregada
   * Se a lista estiver vazia, carrega os dados automaticamente
   * @param codigo - O código do feedback a ser buscado
   * @returns O objeto StatusDocumento completo ou undefined se não encontrado
   */
  async buscarFeedbackPorCodigo(codigo: string): Promise<StatusDocumento | undefined> {
    const statusDocumento = this._state().statusDocumento;
    
    // Se a lista estiver vazia, carrega os dados automaticamente
    if (!statusDocumento || statusDocumento.length === 0) {
      await this.carregarStatusDocumento();
    }
    
    // Busca o feedback na lista (agora garantidamente carregada)
    return this._state().statusDocumento.find(feedback => feedback.codigo === codigo);
  }
}
